import { useLazyQuery, useQuery } from '@apollo/client'
import { useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { DateObject } from '../../helpers/date'
import {
  getEntityCode,
  getEntityId,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { Channel } from '../../types/channel'
import {
  RadioButtonType,
  RateConnectionGenerateInput,
  StructuredData
} from '../../types/rateConnection'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import AccessButtonSection from './AccessButtonSection'
import ChannelSection from './ChannelSection'
import Filters from './Filters'
import ProviderSection from './ProviderSection'
import useStyles from './styles/index.styles'

export default function PriceClassRateConnection() {
  const classes = useStyles()

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN
  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER
  const isRoleAccommodation = getRoleFromStorage() === ROLE.ACCOMMODATION

  const [selectedDates, setSelectedDates] = useState<DateObject[]>([{}])
  const [selectedWeekDaysArray, setSelectedWeekDaysArray] = useState<
    string[][]
  >([[]])
  const [autoCompleteForPriceClass, setAutoCompleteForPriceClass] =
    useState<any>(null)
  const [autoCompleteForProvider, setAutoCompleteForProvider] = useState<any>(
    isRoleProvider
      ? {
          label: getEntityName(),
          value: getEntityId(),
          code: getEntityCode()
        }
      : null
  )
  const [input, setInput] = useState<RateConnectionGenerateInput>({
    inputCurrency: 'IRR',
    outputCurrency: '',
    priceClassID: '',
    date: [],
    providerCode: '',
    selectedRatePlans: [],
    selectedChannelsCode: []
  })

  const [structuredDataAccommodation, setStructuredDataAccommodation] =
    useState<StructuredData[]>([])
  const [structuredDataChannel, setStructuredDataChannel] = useState<
    StructuredData[]
  >([])
  const [selectedAccommodationID, setSelectedAccommodationID] =
    useState<string>('')
  const [radioButtonValue, setRadioButtonValue] = useState<RadioButtonType>(
    isRoleAdmin ? 'PriceClass' : 'RateMultiplier'
  )

  const [getRatePlans, { data: ratePlanData, loading: loadingForRatePlan }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    })

  const [getRoomTypes, { data: roomtypesData, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    })

  const { data: channelData, loading: loadingForChannelData } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Subscribe')} />
      <Filters
        selectedDates={selectedDates}
        setSelectedDates={setSelectedDates}
        autoCompleteForPriceClass={autoCompleteForPriceClass}
        setAutoCompleteForPriceClass={setAutoCompleteForPriceClass}
        input={input}
        setInput={setInput}
        selectedWeekDaysArray={selectedWeekDaysArray}
        setSelectedWeekDaysArray={setSelectedWeekDaysArray}
        radioButtonValue={radioButtonValue}
        setRadioButtonValue={setRadioButtonValue}
        isRoleAdmin={isRoleAdmin}
      />
      <div className={classes.mainSection}>
        <ChannelSection
          structuredDataChannel={structuredDataChannel}
          setStructuredDataChannel={setStructuredDataChannel}
          channelData={channelData}
          loadingForChannelData={loadingForChannelData}
        />
        <AccessButtonSection
          input={input}
          setInput={setInput}
          setAutoCompleteForPriceClass={setAutoCompleteForPriceClass}
          setSelectedDates={setSelectedDates}
          setSelectedWeekDaysArray={setSelectedWeekDaysArray}
          autoCompleteForProvider={autoCompleteForProvider}
          setAutoCompleteForProvider={setAutoCompleteForProvider}
          structuredDataAccommodation={structuredDataAccommodation}
          setStructuredDataAccommodation={setStructuredDataAccommodation}
          structuredDataChannel={structuredDataChannel}
          setStructuredDataChannel={setStructuredDataChannel}
          radioButtonValue={radioButtonValue}
          loadingToGetData={
            loadingForRoomtypes || loadingForRatePlan || loadingForChannelData
          }
        />
        <ProviderSection
          input={input}
          setInput={setInput}
          autoCompleteForProvider={autoCompleteForProvider}
          setAutoCompleteForProvider={setAutoCompleteForProvider}
          structuredDataAccommodation={structuredDataAccommodation}
          setStructuredDataAccommodation={setStructuredDataAccommodation}
          selectedAccommodationID={selectedAccommodationID}
          setSelectedAccommodationID={setSelectedAccommodationID}
          ratePlanData={ratePlanData}
          getRoomTypes={getRoomTypes}
          getRatePlans={getRatePlans}
          roomtypesData={roomtypesData}
          loadingForRoomtypes={loadingForRoomtypes}
          isRoleProvider={isRoleProvider}
          isRoleAccommodation={isRoleAccommodation}
          loadingForRatePlan={loadingForRatePlan}
        />
      </div>
    </div>
  )
}
